import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { AdminPackForm } from "@/components/AdminPackForm";
import { AdminOrders } from "@/components/AdminOrders";
import { AdminSettings } from "@/components/AdminSettings";
import { AdminStarterKit } from "@/components/AdminStarterKit";
import { AdminLeads } from "@/components/AdminLeads";
import { AdminHomepageEditor } from "@/components/AdminHomepageEditor";
import { AdminReviews } from "@/components/AdminReviews";
import { AnalyticsDashboard } from "@/components/AnalyticsDashboard";
import api from "@/lib/api";
import {
  Plus, Edit, Trash2, LogOut, ArrowLeft, Package,
  Image as ImageIcon, X, Tag, GripVertical,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  published: "bg-green-100 text-green-800 border-0",
  coming_soon: "bg-amber-100 text-amber-800 border-0",
  draft: "bg-slate-100 text-slate-600 border-0",
};
const STATUS_LABELS = {
  published: "Published",
  coming_soon: "Coming Soon",
  draft: "Draft",
};

/* ─── Sortable Pack Row ────────────────────────── */
function SortablePackRow({ pack, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: pack._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : "auto",
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-2xl p-3 flex items-center gap-2 ${isDragging ? "shadow-lg ring-2 ring-coral/30" : ""}`}
      data-testid={`admin-pack-item-${pack._id}`}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="touch-none p-1 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing shrink-0"
        data-testid={`drag-handle-${pack._id}`}
        aria-label="Drag to reorder"
      >
        <GripVertical className="w-5 h-5" />
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <h3 className="font-semibold text-sm text-slate-800 truncate">{pack.title}</h3>
          <Badge className={`text-[10px] ${STATUS_STYLES[pack.status]}`}>
            {STATUS_LABELS[pack.status]}
          </Badge>
        </div>
        <p className="text-xs text-slate-500 flex items-center gap-2">
          <span>{pack.category}</span>
          <span className="text-slate-300">|</span>
          <span className="font-semibold">₹{pack.discounted_price ?? pack.price}</span>
          {pack.discounted_price != null && (
            <span className="line-through text-slate-400">₹{pack.price}</span>
          )}
          <span className="text-slate-300">|</span>
          <span className="flex items-center gap-0.5">
            <ImageIcon className="w-3 h-3" />{pack.images?.length || 0}
          </span>
        </p>
      </div>

      <div className="flex gap-1.5 shrink-0">
        <Button
          variant="outline" size="icon" className="h-8 w-8 rounded-lg"
          onClick={() => onEdit(pack)}
          data-testid={`admin-edit-pack-${pack._id}`}
        >
          <Edit className="w-3.5 h-3.5" />
        </Button>
        <Button
          variant="outline" size="icon"
          className="h-8 w-8 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50"
          onClick={() => onDelete(pack._id)}
          data-testid={`admin-delete-pack-${pack._id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

/* ─── Admin Panel ──────────────────────────────── */
export default function AdminPanel() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [editingPack, setEditingPack] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  useEffect(() => {
    fetchPacks();
    fetchCategories();
  }, []);

  async function fetchPacks() {
    try {
      const { data } = await api.get("/admin/packs");
      setPacks(data);
    } catch {
      toast.error("Failed to load packs");
    } finally {
      setLoading(false);
    }
  }

  async function fetchCategories() {
    try {
      const { data } = await api.get("/categories");
      setCategories(data.categories || []);
    } catch {
      setCategories([]);
    }
  }

  async function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = packs.findIndex((p) => p._id === active.id);
    const newIndex = packs.findIndex((p) => p._id === over.id);
    const reordered = arrayMove(packs, oldIndex, newIndex);
    setPacks(reordered);

    try {
      await api.put("/admin/packs/reorder", {
        pack_ids: reordered.map((p) => p._id),
      });
      toast.success("Order saved");
    } catch {
      toast.error("Failed to save order");
      fetchPacks();
    }
  }

  async function handleAddCategory() {
    const name = newCategory.trim();
    if (!name) return;
    try {
      await api.post("/admin/categories", { name });
      toast.success(`Category "${name}" added`);
      setNewCategory("");
      fetchCategories();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to add category");
    }
  }

  async function handleDeleteCategory(name) {
    if (!window.confirm(`Remove category "${name}"?`)) return;
    try {
      await api.delete(`/admin/categories/${encodeURIComponent(name)}`);
      toast.success(`Category "${name}" removed`);
      fetchCategories();
    } catch {
      toast.error("Failed to remove category");
    }
  }

  async function handleDelete(packId) {
    if (!window.confirm("Delete this pack permanently?")) return;
    try {
      await api.delete(`/admin/packs/${packId}`);
      toast.success("Pack deleted");
      fetchPacks();
    } catch {
      toast.error("Failed to delete pack");
    }
  }

  function handleFormClose() {
    setShowForm(false);
    setEditingPack(null);
    fetchPacks();
  }

  async function handleLogout() {
    await logout();
    navigate("/admin/login");
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-panel-container">
      {/* Header */}
      <div className="bg-white border-b border-border sticky top-0 z-10 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="h-9 w-9">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading font-bold text-lg">Admin Panel</h1>
          </div>
          <Button
            variant="ghost" size="icon" onClick={handleLogout}
            className="h-9 w-9 text-slate-500" data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        <Tabs defaultValue="packs" className="w-full">
          <TabsList className="w-full bg-white rounded-2xl p-1 flex flex-wrap gap-1 h-auto">
            {[
              { val: "packs", label: "Packs", tid: "admin-packs-tab" },
              { val: "orders", label: "Orders", tid: "admin-orders-tab" },
              { val: "categories", label: "Categories", tid: "admin-categories-tab" },
              { val: "analytics", label: "Analytics", tid: "admin-analytics-tab" },
              { val: "settings", label: "Settings", tid: "admin-settings-tab" },
              { val: "starter", label: "Starter Kit" },
              { val: "leads", label: "Leads" },
              { val: "homepage", label: "Homepage" },
              { val: "reviews", label: "Reviews" },
            ].map((t) => (
              <TabsTrigger key={t.val} value={t.val}
                className="flex-1 min-w-[70px] rounded-full data-[state=active]:bg-coral data-[state=active]:text-white font-semibold text-xs px-2 py-1.5"
                data-testid={t.tid}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* ── Packs Tab ─────────────────────────── */}
          <TabsContent value="packs">
            <div className="space-y-2 mt-4">
              <Button
                onClick={() => { setEditingPack(null); setShowForm(true); }}
                className="w-full bg-coral hover:bg-coral-hover text-white rounded-full h-11 font-bold"
                data-testid="admin-add-pack-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add New Pack
              </Button>

              {!loading && packs.length > 1 && (
                <p className="text-xs text-slate-400 text-center font-body py-1">
                  Drag the <GripVertical className="w-3 h-3 inline" /> handle to reorder packs
                </p>
              )}

              {loading ? (
                [1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-2xl h-20 animate-pulse" />
                ))
              ) : packs.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 font-body">No packs yet. Create your first pack!</p>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={packs.map((p) => p._id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {packs.map((pack) => (
                        <SortablePackRow
                          key={pack._id}
                          pack={pack}
                          onEdit={(p) => { setEditingPack(p); setShowForm(true); }}
                          onDelete={handleDelete}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </TabsContent>

          {/* ── Orders Tab ────────────────────────── */}
          <TabsContent value="orders">
            <AdminOrders />
          </TabsContent>

          {/* ── Categories Tab ────────────────────── */}
          <TabsContent value="categories">
            <div className="mt-4 space-y-4">
              <div className="flex gap-2">
                <Input
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="New category name"
                  className="rounded-full flex-1"
                  onKeyDown={(e) => e.key === "Enter" && handleAddCategory()}
                  data-testid="admin-new-category-input"
                />
                <Button
                  onClick={handleAddCategory}
                  className="bg-coral hover:bg-coral-hover text-white rounded-full px-5 font-bold"
                  disabled={!newCategory.trim()}
                  data-testid="admin-add-category-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {categories.length === 0 ? (
                  <p className="text-center py-8 text-slate-500 font-body">
                    No categories yet. Add one above!
                  </p>
                ) : (
                  categories.map((cat) => (
                    <div
                      key={cat}
                      className="bg-white rounded-xl px-4 py-3 flex items-center justify-between"
                      data-testid={`admin-category-item-${cat}`}
                    >
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-coral" />
                        <span className="font-semibold text-sm text-slate-700">{cat}</span>
                      </div>
                      <Button
                        variant="ghost" size="icon"
                        className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        onClick={() => handleDeleteCategory(cat)}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
              <p className="text-xs text-slate-400 font-body text-center">
                Categories appear as filter tabs on the public catalog page.
              </p>
            </div>
          </TabsContent>

          {/* ── Settings Tab ─────────────────────── */}
          <TabsContent value="settings">
            <AdminSettings />
          </TabsContent>

          {/* ── Analytics Tab ─────────────────────── */}
          <TabsContent value="analytics">
            <AnalyticsDashboard />
          </TabsContent>

          {/* ── Starter Kit Tab ───────────────────── */}
          <TabsContent value="starter">
            <AdminStarterKit />
          </TabsContent>

          {/* ── Leads Tab ─────────────────────────── */}
          <TabsContent value="leads">
            <AdminLeads />
          </TabsContent>

          {/* ── Homepage Tab ──────────────────────── */}
          <TabsContent value="homepage">
            <AdminHomepageEditor />
          </TabsContent>

          {/* ── Reviews Tab ──────────────────────── */}
          <TabsContent value="reviews">
            <AdminReviews />
          </TabsContent>
        </Tabs>
      </div>

      {showForm && (
        <AdminPackForm pack={editingPack} onClose={handleFormClose} />
      )}
    </div>
  );
}
